import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import FormStepsNav from '../../2_Molecules/FormStepsNav/FormStepsNav'
import FindDealerStep from '../../4_Templates/FindDealerStep/FindDealerStep'
import DateAndTimeStep from '../../4_Templates/DateAndTimeStep/DateAndTimeStep'
import ContactDetailsStep from '../../4_Templates/ContactDetailsStep/ContactDetailsStep'
import SelectVehicleStep from '../../4_Templates/SelectVehicleStep/SelectVehicleStep'
import { languageController } from '../../../controllers/language/languageController'
import StepWrapper from '../../2_Molecules/StepWrapper/StepWrapper'
import ConfirmationStep from '../../4_Templates/ConfirmationStep/ConfirmationStep'
import { convertLangCodeToSingleLetter, displayOutsideComponents, hideOutsideComponents } from '../../../utils/helpers'
import pidController from '../../../controllers/pidController'
import { getEndpointUrl } from '../../../utils/endPoints'
import { handleRequest } from '../../../utils/httpRequest'
import generalController from '../../../controllers/generalController'

function BookTestDrive({ formExtraParams }) {
  const { modelCode, modelOption, isTopSectionHidden, source, language, lastStepBottomContents, initStepTopContents, onlyEVDealers, isEmbeddedOnBnP } = formExtraParams
  const maxStepNum = 4;
  const initStep = (modelCode && modelOption ? 1 : 0);

  // Convert boolean string to bool type.
  const isTopSectionHiddenFormatted = (isTopSectionHidden ? isTopSectionHidden.toLowerCase() === 'true' : false);
  const onlyEVDealersFormatted = (onlyEVDealers ? onlyEVDealers.toLowerCase() === 'true' : false);
  const isEmbeddedOnBnPFormatted = (isEmbeddedOnBnP ? isEmbeddedOnBnP.toLowerCase() === 'true' : false);

  const [currentStep, setCurrentStep] = useState(initStep)
  const [currentProgress, setCurrentProgress] = useState(initStep)
  const [data, setData] = useState({})

  // Display top contents on the init step.
  useEffect(() => {
    const fetchModelData = async () => {
      try {
        // Get model data based on modelCode and modelOption.
        const province = generalController.getMetatagValue('province');

        const res = await handleRequest('get', 
          `${getEndpointUrl('model-search')}/${modelCode}/${modelOption}?lang_code=${language}&prov_code=${province}`);

        if (res.status === 200 && res.data) {
          const vehicleData = res.data;
          setData({
            ...data,
            vehicleData: {
              isEV: vehicleData.is_ev,
              title: vehicleData.carline.title,
              model: vehicleData.carline.url_slug,
              msrp: vehicleData.financial?.msrp,
              year: vehicleData.year.url_slug,
              description: vehicleData.model_year?.description
            }
          })
        }
      } catch (err) {
        console.error('Error ', err)
      }
    }
    if (initStep === 0) {
      displayOutsideComponents(initStepTopContents)
    }
    if (modelCode && modelOption) {
      // fetch the model data of the modelCode.
      fetchModelData()
    }
  }, [])

  const handleNextStep = (newProps) => {
    if (currentStep === maxStepNum) return;

    const nextStep = currentStep + 1;

    // Manage visibility of components outside of the react app.
    if (nextStep === 1) {
      // Hide BaTD Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display BaTD related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
    }

    setCurrentStep(nextStep)

    setData({ ...data, ...newProps })

    setCurrentProgress(prevState => nextStep > prevState ? nextStep : prevState)

    if (!isEmbeddedOnBnPFormatted) {
      window.scrollTo(0, 0)
    }
  }

  // Refresh data and go back to init step.
  const refreshSteps = () => {
    setCurrentStep(0)
    setCurrentProgress(0)
    setData({})
    displayOutsideComponents(initStepTopContents)
  }

  return (
    <div 
      className={`mz-book-test-drive mz-page-top ${isEmbeddedOnBnPFormatted ? 'mz-book-test-drive--bnp' : ''}`}
      data-current-step={currentStep} 
    >
      {!isTopSectionHiddenFormatted && (
        <FormStepsNav
          currentStep={currentStep}
          currentProgress={currentProgress}
          setCurrentStep={setCurrentStep}
          refreshSteps={refreshSteps}
          isActive={(currentStep > 0 && currentStep < maxStepNum)}
          refreshText={languageController.getCopy('global', 'vehicles')}
          navList={[
            {
              step: 1,
              label: languageController.getCopy('global', 'selectDealerNav'),
            },
            {
              step: 2,
              label: languageController.getCopy('global', 'selectDateAndTimeNav'),
            },
            {
              step: 3,
              label: languageController.getCopy('global', 'contactDetailsNav'),
            },
          ]}
        />
      )}
      <div className={`mz-book-test-drive__steps ${isTopSectionHiddenFormatted ? 'mz-book-test-drive__steps--no-steps-nav' : ''}`}>
        {/* Step 0: Select a vehicle */}
        <StepWrapper
          className={`mz-book-test-drive__select-vehicle`}
          stepNum={0}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <SelectVehicleStep
            title={languageController.getCopy('global', 'selectVehicle')}
            handleNextStep={handleNextStep}
          />
        </StepWrapper>
        {/* Step 1: Select a dealer */}
        <StepWrapper
          className={`mz-book-test-drive__select-dealer`}
          type="select-dealer"
          stepNum={1}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <FindDealerStep
            handleNextStep={handleNextStep}
            vehicleData={data.vehicleData}
            title={languageController.getCopy('testDrive', 'step1Title')}
            subTitle={languageController.getCopy('global', 'findDealerSub')}
            isTopSectionHidden={isTopSectionHiddenFormatted}
            isEmbeddedOnBnP={isEmbeddedOnBnPFormatted}
            onlyEVDealers={onlyEVDealersFormatted}
          />
        </StepWrapper>
        {/* Step 2: Choose Date and Time */}
        <StepWrapper
          className={`mz-book-test-drive__date-and-time`}
          stepNum={2}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <DateAndTimeStep
            handleNextStep={handleNextStep}
            title={languageController.getCopy('global', 'selectDateAndTime')}
            subTitle={languageController.getCopy('testDrive', 'step2Subtitle')}
          />
        </StepWrapper>
        {/* Step 3: Enter Contact Details */}
        <StepWrapper
          className={`mz-book-test-drive__contact-details`}
          stepNum={3}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ContactDetailsStep
            handleNextStep={handleNextStep}
            title={languageController.getCopy('global', 'fillContactForm')}
            submitButtonText={languageController.getCopy('testDrive', 'step4Submit')}
            dealerData={data.dealerData}
            dateAndTimeData={data.dateAndTime}
            vehicleData={data.vehicleData}
            formSource={source}
            formPid={pidController.getPid('requestQuote')}
            formEndPointUrl={getEndpointUrl('request-quote')}
            formLanguage={convertLangCodeToSingleLetter(language)}
          />
        </StepWrapper>
        {/* Step 4: Show Confirmation */}
        <StepWrapper
          className={`mz-book-test-drive__confirmation`}
          stepNum={4}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ConfirmationStep
            vehicleData={data.vehicleData}
            dateAndTime={data.dateAndTime}
            dealerData={data.dealerData}
            contactDetails={data.contactDetails}
          />
        </StepWrapper>
      </div>
    </div>
  )
}

BookTestDrive.propTypes = {
  formExtraParams: PropTypes.object.isRequired
}

BookTestDrive.defaultProps = {
  formExtraParams: {}
}

export default BookTestDrive